import { useEffect, useMemo, useState } from "react";
import { Download, Loader2, Sparkles, Volume2 } from "lucide-react";
import { API, api, apiErrToStr, fmtDate } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const SPEEDS = [
  { value: 130, label: "Lenta" },
  { value: 155, label: "Normal" },
  { value: 185, label: "Rapida" },
];

export default function AudioGenerator() {
  const { activeTenantId, tenants, user } = useAuth();
  const [nome, setNome] = useState("");
  const [mensagem, setMensagem] = useState("");
  const [velocidade, setVelocidade] = useState(155);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const activeTenant = useMemo(
    () => tenants.find((tenant) => String(tenant.id) === String(activeTenantId)),
    [activeTenantId, tenants],
  );
  const tenantName = activeTenant?.nome || user?.tenant?.nome || "cliente selecionado";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/audio-generator", { params: { _ts: Date.now() } });
      setItems(data || []);
    } catch (err) {
      setError(apiErrToStr(err?.response?.data?.detail || "Nao foi possivel carregar os audios."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [activeTenantId]);

  const generate = async (event) => {
    event.preventDefault();
    setError("");
    setNotice("");
    setCreating(true);
    try {
      const { data } = await api.post("/audio-generator", { nome, mensagem, voz: "pt-BR", velocidade: Number(velocidade) });
      setItems((current) => [data, ...current]);
      setNome("");
      setMensagem("");
      setNotice("Audio criado e liberado para uso em URAs, filas e anuncios.");
    } catch (err) {
      setError(apiErrToStr(err?.response?.data?.detail || "Nao foi possivel gerar o audio."));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="audio-generator-page">
      <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Mensagens operacionais</div>
          <h1 className="text-2xl font-semibold">Gerador de audios</h1>
          <p className="text-sm text-[color:var(--text-secondary)] mt-1">Crie mensagens em portugues do Brasil para o cliente selecionado.</p>
        </div>
        <div className="text-xs text-[color:var(--text-secondary)]"><span className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] mr-2">Cliente</span>{tenantName}</div>
      </div>

      <form className="sbc-card p-5 grid gap-4" onSubmit={generate}>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 flex items-center justify-center border border-[color:var(--border-default)] text-[color:var(--accent-blue)]">
            <Sparkles size={18} />
          </div>
          <div>
            <h2 className="text-sm font-semibold">Nova mensagem</h2>
            <p className="text-xs text-[color:var(--text-secondary)]">A voz sera sintetizada no servidor e salva como WAV.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,1fr)_180px]">
          <label className="block">
            <span className="block text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] mb-2">Nome</span>
            <input className="sbc-input w-full" value={nome} onChange={(event) => setNome(event.target.value)} placeholder="Ex: Aviso de atendimento" maxLength={96} required />
          </label>
          <label className="block">
            <span className="block text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] mb-2">Velocidade</span>
            <select className="sbc-input w-full" value={velocidade} onChange={(event) => setVelocidade(event.target.value)}>
              {SPEEDS.map((speed) => <option key={speed.value} value={speed.value}>{speed.label}</option>)}
            </select>
          </label>
        </div>

        <label className="block">
          <span className="block text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] mb-2">Mensagem em portugues do Brasil</span>
          <textarea className="sbc-input w-full min-h-32 resize-y" value={mensagem} onChange={(event) => setMensagem(event.target.value)} placeholder="Digite a mensagem que sera reproduzida na chamada." maxLength={800} required />
          <span className="block text-[11px] text-[color:var(--text-muted)] mt-2">{mensagem.length}/800 caracteres</span>
        </label>

        {error && <div className="text-xs text-[color:var(--accent-red)]">{error}</div>}
        {notice && <div className="text-xs text-[color:var(--accent-green)]">{notice}</div>}
        <div className="flex justify-end">
          <button className="sbc-btn sbc-btn-primary" type="submit" disabled={creating}>
            {creating ? <Loader2 size={15} className="animate-spin" /> : <Volume2 size={15} />}
            {creating ? "Gerando..." : "Gerar audio"}
          </button>
        </div>
      </form>

      <section className="sbc-card overflow-hidden">
        <div className="px-5 py-4 border-b border-[color:var(--border-default)] flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold">Audios gerados</h2>
            <p className="text-xs text-[color:var(--text-secondary)] mt-1">Eles tambem aparecem no cadastro de anuncios deste cliente.</p>
          </div>
          <span className="text-xs font-mono text-[color:var(--text-muted)]">{items.length}</span>
        </div>
        {loading ? (
          <div className="px-5 py-8 text-sm text-[color:var(--text-muted)]">Carregando audios...</div>
        ) : items.length === 0 ? (
          <div className="px-5 py-8 text-sm text-[color:var(--text-muted)]">Nenhum audio gerado para este cliente.</div>
        ) : (
          <div className="divide-y divide-[color:var(--border-default)]">
            {items.map((item) => {
              const audioUrl = `${API}/audio-generator/${item.id}/audio`;
              return <div key={item.id} className="px-5 py-4 grid gap-3 lg:grid-cols-[minmax(220px,1fr)_minmax(260px,1.4fr)_300px_auto] lg:items-center">
                <div>
                  <div className="font-medium text-sm">{item.nome}</div>
                  <div className="text-[11px] text-[color:var(--text-muted)] mt-1">{fmtDate(item.created_at)} · pt-BR · {item.tts_rate || 155}</div>
                </div>
                <div className="text-xs text-[color:var(--text-secondary)] line-clamp-2">{item.mensagem}</div>
                <audio controls preload="none" className="w-full h-9" src={audioUrl}>Seu navegador nao suporta audio.</audio>
                <a className="sbc-btn" href={`${audioUrl}?download=true`} title="Baixar audio" aria-label={`Baixar ${item.nome}`}>
                  <Download size={15} />
                </a>
              </div>;
            })}
          </div>
        )}
      </section>
    </div>
  );
}
